import { useState, useEffect, useCallback } from 'react';
import { useWeeklyReview } from './useWeeklyReview';

const REMINDER_HOUR = 14; // 2 PM
const STORAGE_KEY = 'weekly-review-reminder-dismissed';

export function useReviewReminder() {
  const { hasReviewedCurrentWeek, loading } = useWeeklyReview();
  const [showReminder, setShowReminder] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');

  // Check if it's Friday afternoon
  const isFridayAfternoon = useCallback(() => {
    const now = new Date();
    const dayOfWeek = now.getDay(); // 5 = Friday
    const hour = now.getHours();
    return dayOfWeek === 5 && hour >= REMINDER_HOUR;
  }, []);

  // Check if reminder was dismissed today
  const wasReminderDismissedToday = useCallback(() => {
    const dismissedDate = localStorage.getItem(STORAGE_KEY);
    if (!dismissedDate) return false;

    const today = new Date().toISOString().split('T')[0];
    return dismissedDate === today;
  }, []);

  // Dismiss the reminder for today
  const dismissReminder = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];
    localStorage.setItem(STORAGE_KEY, today);
    setShowReminder(false);
  }, []);

  // Request notification permission
  const requestNotificationPermission = useCallback(async () => {
    if (!('Notification' in window)) return 'unsupported';

    const permission = await Notification.requestPermission();
    setNotificationPermission(permission);
    return permission;
  }, []);

  // Send browser notification
  const sendNotification = useCallback(() => {
    if (notificationPermission !== 'granted') return;

    new Notification('Weekly Review Time', {
      body: 'It\'s Friday afternoon - time for your weekly review!',
      icon: '/logo-192.png',
      tag: 'weekly-review-reminder',
      requireInteraction: true
    });
  }, [notificationPermission]);

  // Check reminder status
  useEffect(() => {
    if (loading) return;

    // Check notification permission
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }

    const checkReminder = () => {
      const shouldShow = isFridayAfternoon() &&
                        !hasReviewedCurrentWeek() &&
                        !wasReminderDismissedToday();

      setShowReminder(shouldShow);

      // Send notification if conditions met and permission granted
      if (shouldShow && notificationPermission === 'granted') {
        sendNotification();
      }
    };

    // Initial check
    checkReminder();

    // Check every minute
    const interval = setInterval(checkReminder, 60000);

    return () => clearInterval(interval);
  }, [loading, hasReviewedCurrentWeek, isFridayAfternoon, wasReminderDismissedToday, notificationPermission, sendNotification]);

  return {
    showReminder,
    dismissReminder,
    isFridayAfternoon: isFridayAfternoon(),
    hasReviewedCurrentWeek: hasReviewedCurrentWeek(),
    notificationPermission,
    requestNotificationPermission,
    loading
  };
}
